import { formatIDR } from "@kanata/shared";

import type { ApiSku } from "@/lib/api";
import type { Addon } from "@/lib/pdp-pricing";
import { addonThumbnail } from "@/lib/product-images";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { SectionNumber } from "./SectionNumber";

/**
 * The optional accessories, as one collapsible step of the configurator.
 *
 * Open by default — an add-on nobody sees is an add-on nobody buys — but
 * collapsible so a shopper who only wants the desk can fold it away. Each row
 * is a card with the accessory's own photograph and price; every selection
 * becomes its own cart line, added together with the desk in one action.
 */
export function AddonList({
  n,
  addons,
  selectedIds,
  onToggle,
  disabled,
}: {
  n: number;
  addons: Addon[];
  selectedIds: string[];
  onToggle: (skuId: string, on: boolean) => void;
  disabled?: boolean;
}) {
  const loaded = addons.filter((a): a is Addon & { sku: ApiSku } => Boolean(a.sku));
  if (loaded.length === 0) return null;

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="addons"
      className="rounded-2xl border border-ink/12 px-4"
    >
      <AccordionItem value="addons" className="border-b-0">
        <AccordionTrigger className="py-4 hover:text-ink">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <SectionNumber n={n} />
            <span>
              {n}. Aksesori tambahan
            </span>
            <span className="text-sm font-normal text-ink/55">
              {selectedIds.length > 0 ? `${selectedIds.length} dipilih` : "opsional"}
            </span>
          </span>
        </AccordionTrigger>
        <AccordionContent className="text-ink">
          <p className="mb-3 text-sm text-ink/65">
            Harga terpisah. Semua yang dipilih masuk keranjang bersama mejanya.
          </p>
          <ul className="space-y-2">
            {loaded.map((a) => (
              <AddonCard
                key={a.skuId}
                addon={a}
                checked={selectedIds.includes(a.skuId)}
                onToggle={(on) => onToggle(a.skuId, on)}
                disabled={disabled}
              />
            ))}
          </ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function AddonCard({
  addon,
  checked,
  onToggle,
  disabled,
}: {
  addon: Addon & { sku: ApiSku };
  checked: boolean;
  onToggle: (on: boolean) => void;
  disabled?: boolean;
}) {
  const { sku } = addon;
  const thumb = addonThumbnail(addon);
  const id = `addon-${sku.id}`;
  const soldOut = Boolean(sku.soldOut);
  const off = soldOut || disabled;

  return (
    <li>
      <Label
        htmlFor={id}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-base font-normal transition-colors",
          checked ? "border-primary bg-brand-tint" : "border-ink/12 bg-white hover:border-ink/25",
          off && "cursor-not-allowed opacity-60 hover:border-ink/12",
        )}
      >
        <Checkbox
          id={id}
          checked={checked}
          disabled={off}
          onCheckedChange={(v) => onToggle(v === true)}
        />
        <span className="size-14 shrink-0 overflow-hidden rounded-lg bg-cream">
          {thumb && (
            <img src={thumb.publicUrl} alt="" loading="lazy" className="size-full object-cover" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-ink">{sku.label}</span>
          {soldOut && (
            <Badge variant="soldOut" className="mt-1">
              Stok habis
            </Badge>
          )}
        </span>
        <span className="shrink-0 text-sm font-medium tabular-nums text-ink">
          {formatIDR(sku.priceIdr)}
        </span>
      </Label>
    </li>
  );
}
